<template>
    <div
        class="vld-parent"
        :class="{ 'with-border': !collectionChart }"
        :style="collectionChart ? 'margin: 5px' : ''"
    >
        <pin-dialog v-model="pinDialog.show" :pinParams="pinParams" :title="pinDialog.title"/>

        <chart
            ref="bigFiveChart"
            :class="collectionChart ? 'mx-1' : ''"
            :config="updatedLocalConfig"
            :collectionChart="collectionChart"
            :pinTitle="getTitle"
            @update:pinTitle="getTitle = $event"
            stopClick
        >
            <template #chart-title>
                <div>
                    <div class="d-flex align-center">
                        <div ref="title" v-safe-html="getTitle"></div>
                        <fe-info-tip
                            v-if="incidentChart && chartInfoTooltip"
                            :tooltip="chartInfoTooltip"
                            aria-label="Chart information"
                            icon="far fa-info-circle"
                            color="#495B8F"
                            direction="right"
                            size="16px"
                            class="ml-2"
                        />
                    </div>
                    <div v-if="incidentChart && showViewModeToggle" class="chart-view-mode-toggle">
                        <v-radio-group
                            v-model="viewMode"
                            row
                            dense
                            hide-details
                            class="mt-3 mb-0"
                            aria-label="Chart view: count or student rate"
                            data-test="big-five-chart-view-mode"
                            @keydown.native.left.prevent="viewMode = 'count'"
                            @keydown.native.right.prevent="viewMode = 'rate'"
                        >
                            <v-radio
                                value="count"
                                :label="countRadioLabel"
                                :data-test="`big-five-chart-incident-count-radio`"
                            ></v-radio>
                            <v-radio
                                value="rate"
                                label="Student Rate"
                                :data-test="`big-five-chart-student-rate-radio`"
                            ></v-radio>
                        </v-radio-group>
                    </div>
                </div>
            </template>
            <template #before-menu v-if="showTools">
                <fe-icon-btn
                    v-if="!collectionChart"
                    useIcon="fas fa-thumbtack"
                    @click="pinDialog.show = true; pinDialog.title = 'Incidents ' + chartParams.title"
                />

                <fe-icon-btn useIcon="fas fa-expand-alt" @click="doExpand"/>
            </template>
            <template #after-chart v-if="collectionChart">
                <div v-if="subtitleFilters" class="d-flex justify-center chart-with-filters">
                    <template v-for="filter in subtitleFilters">
                        <fe-chip>{{ filter }}
                            <template #tooltip>{{ filter }}</template>
                        </fe-chip>
                    </template>
                    <span
                        v-if="moreFilters"
                        class="show-more-filters-btn"
                        @click="showMoreFilters = !showMoreFilters"
                    >
                            <u>{{ showMoreFilters ? 'Show More' : 'Show Less' }}</u>
                    </span>
                </div>
            </template>
        </chart>
        <div v-if="!collectionChart && subtitleFilters" class="d-flex justify-center chart-with-filters">
            <template v-for="filter in subtitleFilters">
                <fe-chip>{{ filter }}
                    <template #tooltip>{{ filter }}</template>
                </fe-chip>
            </template>

            <span
                v-if="moreFilters"
                class="show-more-filters-btn"
                @click="showMoreFilters = !showMoreFilters"
            >
                    <u>{{ showMoreFilters ? 'Show More' : 'Show Less' }}</u>
            </span>
        </div>

        <div
            v-if="!incidentChart"
            :class="!collectionChart ? 'big5-date-picker' : 'big5-date-picker-collection'"
            :style="collectionChart ? {position: 'relative', bottom: getHeight, 'margin-right': getMargin} : {}"
        >
            <fe-title-select
                v-model="selectedRange"
                fontColor="#7e8494"
                fontSize="12px"
                itemText="name"
                itemValue="value"
                placeholder="Change date range"
                :items="rangeItems"
                @input="updateSingleChart"
                ref="incidents-chart-date-picker"
            />
        </div>

        <div class="loader" v-if="showLoader">
            <v-progress-circular
                class="loader-spinner"
                color="primary"
                indeterminate
            />
        </div>
    </div>
</template>

<script>
import PinDialog from '@/components/common/PinDialog'
import {mapState} from 'vuex'
import Chart from "./Chart";

// Full month name → calendar month (1–12). Use -1 only when comparing to JS Date.getMonth() (0–11).
const MONTH_NAME_MAP = Object.freeze({
    January: 1, February: 2, March: 3, April: 4, May: 5, June: 6,
    July: 7, August: 8, September: 9, October: 10, November: 11, December: 12
})

// Tooltip width by chart width (px). Used by getIncidentTooltipWidthFromChart.
const CHART_WIDTH_NARROW_MAX = 400
const CHART_WIDTH_MID_MAX = 600
const TOOLTIP_WIDTH_NARROW = 160
const TOOLTIP_WIDTH_MID = 210
const TOOLTIP_WIDTH_WIDE = 310
const TOOLTIP_WIDTH_MAX_RATIO = 0.85

export default {
    name: 'BigFiveChart',

    components: {Chart, PinDialog},

    props: {
        config: {required: true},
        height: {
            type: Number,
            default: 350
        },
        showTools: {
            type: Boolean,
            default: true
        },
        collectionChart: {
            type: Boolean,
            default: false
        },
        collectionChartData: {
            type: Object
        },
        incidentChart: {
            type: Boolean,
            default: false
        },
        pinTitle: {
            type: String,
            default: null
        },
        appliedFilters: {
            type: Array,
            default() {
                return []
            }
        },
        // Saved view mode ('count' | 'rate') for incident charts restored from a
        // pinned Collection. Null means "no saved preference" -> falls back to 'count'.
        initialViewMode: {
            type: String,
            default: null
        }
    },

    data() {
        let me = this
        return {
            tooltip: function () {
                return this.series.name + " " + this.x + " " + this.y
            },
            showLoader: false,
            viewMode: 'count',
            pinDialog: {
                show: false,
                title: ''
            },
            rangeItems: [{
                name: 'Last 30 Days',
                value: 30
            }, {
                name: 'Last 60 Days',
                value: 60
            }, {
                name: 'Last 90 Days',
                value: 90
            }],
            seriesColors: ['#2B87FF', '#F59B00', '#08916D', '#529EE0', '#52B7D8', '#D9A6C2', '#FFB03B', '#54A77B', '#4FD2D2', '#F0E442', '#E287B2'],
            selectedRange: null,
            stacked: false,
            categories: {},
            localCategories: [],
            localConfig: {},
            updatedLocalConfig: {},
            chartParams: {},
            pinName: null,
            showMoreFilters: true,
            moreFilters: false,
            extraConfigs: {
                chart: {
                    marginBottom: 90,
                    marginTop: 90,
                    marginLeft: 65,
                    marginRight: 50,
                    spacingBottom: 5,
                    zoomType: 'xy',
                    height: 350,
                    type: 'column'
                },
                title: {
                    useHTML: true,
                    align: 'left',
                    style: {
                        'border-bottom': '1px solid #E0E1E6',
                        'display': 'block',
                        'width': '100%'
                    },
                    text: ''
                },
                plotOptions: {
                    series: {
                        point: {
                            events: {
                                click(e) {
                                    let cfg = me.localConfig.config
                                    me.$emit('chartClicked', cfg.x_field, cfg.data.find(r => r[cfg.x_field] === e.point.category && r.school_year_name === e.point.series.name), e, me.localConfig.baseParams)
                                }
                            }
                        },
                        stacking: false,
                        dataLabels: {
                            enabled: true,
                            style: {
                                textOutline: 0
                            },
                            formatter: function() {
                                if (me.incidentChart && me.showViewModeToggle && me.viewMode === 'rate') {
                                    return me.formatRate(this.y)
                                }
                                return this.y
                            }
                        },
                        size: '100%'
                    }
                },
                yAxis: {
                    minPadding: 0,
                    maxPadding: 0.05,
                    endOnTick: false,
                    minimum: undefined,
                    maximum: undefined,
                    labels: {
                        style: {
                            fontSize: '10px'
                        },
                        formatter: function() {
                            if (me.incidentChart && me.showViewModeToggle && me.viewMode === 'rate') {
                                return me.formatRate(this.value)
                            }
                            return this.value
                        }
                    },
                    tickInterval: undefined,
                    title: {
                        text: ''
                    }
                },
                xAxis: {
                    categories: [],
                    minPadding: 0,
                    maxPadding: 0,
                    labels: {
                        formatter() {
                            return (this.value + '').substring(0, 10)
                        },
                        style: {
                            fontSize: '10px',
                            color: 'gray'
                        }
                    }
                },
                tooltip: {
                    enabled: true,
                    shared: false,
                    positioner: function (labelWidth, labelHeight, point) {
                        let chart = this.chart
                        let numBars = (chart.xAxis && chart.xAxis[0] && chart.xAxis[0].categories)
                            ? chart.xAxis[0].categories.length
                            : (chart.series[0] && chart.series[0].points ? chart.series[0].points.length : 0)
                        let plotWidth = chart.plotWidth
                        // With one bar, always put tooltip to the right so it doesn't get cut off on the left
                        let rightHalf = numBars > 1 && point.plotX > (plotWidth / 2)
                        let hoverPoint = chart.hoverPoint
                        let barHalf = (hoverPoint && hoverPoint.pointWidth != null ? hoverPoint.pointWidth : 0) / 2
                        let x = rightHalf
                            ? chart.plotLeft + point.plotX - barHalf - labelWidth - 2
                            : chart.plotLeft + point.plotX + 10
                        let y = chart.plotTop + point.plotY - labelHeight - 8
                        if (y < chart.plotTop) {
                            y = chart.plotTop
                        }
                        return { x, y }
                    },
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    useHTML: true,
                    formatter: function() {
                        // Custom tooltips only when incident chart context (fromIncidents)
                        if (!me.incidentChart) {
                            return this.series.name + ' ' + this.x + ' ' + this.y
                        }
                        // No tooltips for "top 20" (student) chart
                        if (me.chartParams?.chartType === 'student') {
                            return false
                        }
                        let chart = this.series?.chart
                        let tooltipWidth = me.getIncidentTooltipWidthFromChart(chart)
                        let wrap = (content) => '<div style="width: ' + tooltipWidth + 'px; white-space: normal; overflow-wrap: break-word;">' + content + '</div>'
                        if (this.series.name === 'Cumulative Rate' && this.point.customData) {
                            let pointData = this.point.customData
                            let rateDisplay = me.formatRateDisplay(pointData.y)
                            let content = `<b>${pointData.startDate} - ${pointData.endDate}</b><br/>Cumulative Rate: <b>${rateDisplay} of students</b><br/><span style="color: #495B8F;"><i>Displaying the cumulative rate of students with at least one incident from ${pointData.startDate} to ${pointData.endDate}.</i></span>`
                            return wrap(content)
                        }
                        
                        // Rest of custom tooltip (bar content, etc.)
                        if (!me.chartParams?.chartType) {
                            return this.series.name + " " + this.x + " " + this.y
                        }

                        // Find the original data record
                        let cfg = me.localConfig.config
                        let dataRecord = cfg.data.find(r => 
                            r[cfg.x_field] === this.x && 
                            r.school_year_name === this.series.name
                        )

                        if (!dataRecord) {
                            return this.series.name + " " + this.x + " " + this.y
                        }

                        let totalCount = dataRecord.total_count
                        let studentCount = dataRecord.distinct_student_count
                        let enrolledStudents = dataRecord.enrolled_students
                        let rate = dataRecord.rate
                        let ratePercent = me.formatRate(rate)

                        let chartType = me.chartParams.chartType
                        let isCountMode = me.viewMode === 'count'

                        // Ensure correct year is assigned to the month based on where it falls in the school year
                        let incidentMonthInfo = null
                        if (chartType === 'incident_month') {
                            let monthName = this.x
                            let monthNum = MONTH_NAME_MAP[monthName]
                            let year = ''
                            if (dataRecord.year_start && dataRecord.year_end && monthNum !== undefined) {
                                let rangeStart = me.$dayjs(dataRecord.year_start)
                                let rangeEnd = me.$dayjs(dataRecord.year_end)
                                let firstInStartYear = rangeStart.clone().month(monthNum - 1).startOf('month')
                                let firstInEndYear = rangeEnd.clone().month(monthNum - 1).startOf('month')
                                if (!firstInStartYear.isBefore(rangeStart) && !firstInStartYear.isAfter(rangeEnd)) {
                                    year = firstInStartYear.year().toString()
                                } else if (!firstInEndYear.isBefore(rangeStart) && !firstInEndYear.isAfter(rangeEnd)) {
                                    year = firstInEndYear.year().toString()
                                } else {
                                    year = dataRecord.school_year_name || rangeStart.year().toString()
                                }
                            } else {
                                year = dataRecord.school_year_name || ''
                            }
                            incidentMonthInfo = { monthName, monthNum, year }
                        }
                        let tooltipTitle = incidentMonthInfo
                            ? (incidentMonthInfo.monthNum !== undefined && incidentMonthInfo.year)
                                ? me.$dayjs(`${incidentMonthInfo.year}-${String(incidentMonthInfo.monthNum).padStart(2, '0')}-01`).format('MMM YYYY')
                                : (incidentMonthInfo.monthName + ' ' + incidentMonthInfo.year)
                            : this.x

                        let content = '<b>' + tooltipTitle + '</b><br/>'

                        if (isCountMode) {
                            // COUNT mode tooltips
                            switch (chartType) {
                                case 'incident_behavior_type':
                                    content += 'Count: <b>' + totalCount.toLocaleString() + ' incident codes</b><br/>'
                                    content += 'Student Rate: <b>' + ratePercent + ' of students</b>'
                                    break
                                case 'incident_behavior':
                                    content += 'Count: <b>' + totalCount.toLocaleString() + ' incident codes</b><br/>'
                                    content += 'Student Count: <b>' + studentCount.toLocaleString() + ' of ' + enrolledStudents.toLocaleString() + ' students</b>'
                                    break
                                case 'incident_response':
                                    content += 'Count: <b>' + totalCount.toLocaleString() + ' incident responses</b><br/>'
                                    content += 'Student Count: <b>' + studentCount.toLocaleString() + ' of ' + enrolledStudents.toLocaleString() + ' students</b>'
                                    break
                                case 'incident_month':
                                    content += 'Count: <b>' + totalCount.toLocaleString() + ' incident events</b><br/>'
                                    content += 'Student Rate: <b>' + ratePercent + ' of students</b>'
                                    break
                                case 'incident_weekday':
                                    content += 'Count: <b>' + studentCount.toLocaleString() + ' incident reports</b>'
                                    break
                                case 'student_school':
                                    content += 'Student Count: <b>' + studentCount.toLocaleString() + ' of ' + enrolledStudents.toLocaleString() + ' students who attend this school</b><br/>'
                                    content += 'Student Rate: <b>' + ratePercent + ' of students who attend this school</b>'
                                    break
                                case 'incident_hour':
                                case 'incident_school':
                                case 'incident_location':
                                    content += 'Count: <b>' + totalCount.toLocaleString() + ' incident events</b>'
                                    break
                            }
                        } else {
                            // RATE mode tooltips
                            switch (chartType) {
                                case 'incident_behavior_type':
                                    content += 'Student Rate: <b>' + ratePercent + ' of students</b><br/>'
                                    content += 'Student Count: <b>' + studentCount.toLocaleString() + ' of ' + enrolledStudents.toLocaleString() + ' students</b>'
                                    break
                                case 'incident_month':
                                    content += 'Student Rate: <b>' + ratePercent + ' of students</b><br/>'
                                    content += 'Student Count: <b>' + studentCount.toLocaleString() + ' of ' + enrolledStudents.toLocaleString() + ' students</b>'
                                    break
                                case 'student_school':
                                    content += 'Student Rate: <b>' + ratePercent + ' of students who attend this school</b><br/>'
                                    content += 'Student Count: <b>' + studentCount.toLocaleString() + ' of ' + enrolledStudents.toLocaleString() + ' students who attend this school</b>'
                                    break
                                case 'incident_behavior':
                                case 'incident_response':
                                case 'incident_weekday':
                                case 'incident_hour':
                                case 'incident_school':
                                case 'incident_location':
                                    content += 'Student Rate: <b>' + ratePercent + ' of students</b><br/>'
                                    content += 'Student Count: <b>' + studentCount.toLocaleString() + ' of ' + enrolledStudents.toLocaleString() + ' students</b>'
                                    break
                            }
                        }

                        let dateRange = ''
                        if (chartType === 'incident_month' && incidentMonthInfo) {
                            if (!isCountMode) {
                                let monthYearLabel = (incidentMonthInfo.monthNum !== undefined && incidentMonthInfo.year)
                                    ? me.$dayjs(`${incidentMonthInfo.year}-${String(incidentMonthInfo.monthNum).padStart(2, '0')}-01`).format('MMMM YYYY')
                                    : (incidentMonthInfo.monthName + ' ' + incidentMonthInfo.year)
                                dateRange = '<span style="color: #495B8F;"><i>Displaying the ' + ratePercent + ' of students with at least one incident in ' + monthYearLabel + '.</i></span>'
                            }
                        } else if (chartType !== 'incident_month') {
                            dateRange = dataRecord.school_year_name || ''
                        }

                        if (dateRange) {
                            content += dateRange.startsWith('<')
                                ? '<br/>' + dateRange
                                : '<br/><span style="color: #495B8F;">' + dateRange + '</span>'
                        }

                        return wrap(content)
                    }
                },
                credits: {
                    enabled: false
                },
                navigation: {
                    buttonOptions: {
                        enabled: false
                    }
                },
                legend: {
                    align: 'left',
                    shadow: false,
                },
                series: [],
                dash_id: null,
                saved_search_id: null,
                index: null
            }
        }
    },

    computed: {
        ...mapState('global', ['currentYear']),

        pinParams() {
            let p = {...this.localConfig.baseParams}
            p.dashboard_saved_search_type_id = 3
            p.chart_type = this.chartParams.chartType
            // Persist the selected view mode so the Collection pin defaults to the
            // same "Incident Count" / "Student Rate" selection when reopened. Only
            // meaningful for incident charts that expose the toggle.
            if (this.incidentChart && this.showViewModeToggle) {
                p.config_text = {viewMode: this.viewMode}
            }
            return p
        },

        series() {
            this.extraConfigs.yAxis.minimum = undefined
            this.extraConfigs.yAxis.maximum = undefined
            
            let series = []
            if (this.localConfig?.config && this.localConfig.config.data && this.categories.length) {
                let config = this.localConfig.config
                let data = this.$_.cloneDeep(this.localConfig.config.data)

                if (this.chartParams.chartType.includes('pyramid')) {
                    console.warn('pyramid charts have been moved to their own charting. Use IncidentRange.vue')
                } else {
                    data.forEach((rec, i) => {
                        let exists = series.find(serie => {
                            return serie.name === rec.school_year_name
                        })
                        if (!exists) {
                            series.push({
                                name: rec.school_year_name,
                                schoolYearId: rec.school_year_id,
                                color: this.seriesColors[series.length],
                                data: []
                            })
                        }
                    })
                    
                    series.sort((a, b) => a.name.localeCompare(b.name))

                    series.forEach(serie => {
                        if (this.categories.length > 0) {
                            this.categories.forEach(cat => {
                                let rec = data.find(d => d[config.x_field] === cat && serie.name === d.school_year_name)
                                let value = this.viewMode === 'rate' ? parseFloat(rec?.rate ?? 0) : parseFloat(rec?.total_count ?? 0)
                                this.minMax(value)
                                serie.data.push(value)
                            })
                        } else {
                            data.forEach(d => {
                                let value = this.viewMode === 'rate' ? parseFloat(d?.rate ?? 0) : parseFloat(d?.total_count ?? 0)
                                this.minMax(value)
                                serie.data.push(value)
                            })
                        }
                    })
                    this.localCategories = this.categories
                    this.extraConfigs.plotOptions.series.dataLabels.rotation = 0
                }
            }

            this.extraConfigs.title.text = this.localConfig.config.title
            this.extraConfigs.chart.height = this.height
            this.extraConfigs.xAxis.categories = this.localCategories
            
            // Update y-axis title based on view mode
            if (this.incidentChart && this.showViewModeToggle) {
                this.extraConfigs.yAxis.title.text = this.viewMode === 'rate' ? 'Student Rate' : this.countRadioLabel
            }
            if (this.config && this.config.search_id) {
                this.extraConfigs.dash_id = this.config.search_id.dashboard_id
                this.extraConfigs.saved_search_id = this.config.search_id.saved_search_id
            }
            if (this.config && this.config.index) {
                this.extraConfigs.index = this.config.index
            }

            let cumulativeSeries = this.buildCumulativeRateSeries()
            if (cumulativeSeries) series.push(cumulativeSeries)

            return series
        },

        getHeight() {
            let mod = this.appliedFilters.length || this.localConfig.appliedFilters ? 10 : 55
            return this.height ? (this.height - mod).toString() + 'px' : '340px'
        },

        getMargin() {
            return !this.showTools ? '50px' : '0px'
        },

        getTitle: {
            get() {
                let title = this.pinName || this.pinTitle || this.config.title || ''
                // Replace "Rostered School" with "School of Attendance"
                if (title && typeof title === 'string') {
                    title = title.replace(/Rostered School/gi, 'School of Attendance')
                }
                this.pinName = title
                return title
            },
            set(v) {
                this.pinName = v
            }
        },

        subtitleFilters() {
            if (!this.collectionChart && this.appliedFilters.length) {
                let filterArr = this.appliedFilters
                this.moreFilters = filterArr.length > 3
                return this.showMoreFilters && this.moreFilters ? filterArr.slice(0, 3) : filterArr
            } else if (this.collectionChart && this.localConfig.appliedFilters) {
                let filters = this.localConfig.appliedFilters
                if (filters) {
                    let filterArr = filters.split('|').filter(f => f !== '')
                    this.moreFilters = filterArr.length > 3
                    return this.showMoreFilters && this.moreFilters ? filterArr.slice(0, 3) : filterArr
                }
            }
        },

        showViewModeToggle() {
            // Only show toggle for incident charts that have rate data available
            if (!this.incidentChart) return false
            if (!this.localConfig?.config?.data?.length) return false
            // Check if any record has a rate field
            return this.localConfig.config.data.some(rec => rec.hasOwnProperty('rate'))
        },

        chartInfoTooltip() {
            if (!this.incidentChart || !this.chartParams?.chartType) return ''
            
            const tooltips = {
                'incident_behavior_type': 'This chart displays the number of incident codes by type (incident count) or the percentage of students with at least one incident code of that type (student rate).',
                'incident_behavior': 'This chart displays the number of incidents by code (incident count) or the percentage of students with at least one report of the incident code (student rate).',
                'incident_response': 'This chart displays the count of specific responses (e.g., suspension) or the percentage of students with at least one incident response of that type (student rate).',
                'incident_month': 'This chart shows incident events (or incident IDs) by month. View events by total incident count, student rate, or a cumulative student rate over a longer period of time.',
                'incident_weekday': 'This chart shows incident events (or incident IDs) by weekday. View events by the total incident count or a student rate.',
                'incident_hour': 'This chart shows incident events (or incident IDs) by hour when they occurred. View events by the total incident count or a student rate.',
                'incident_school': 'This chart shows incident events (or incident IDs) by the school where they occurred. View events by the total incident count or a student rate.',
                'incident_location': 'This chart shows the location where incident events (or incident IDs) took place. View events by the total incident count or the student rate.',
                'student': 'This list shows the students with the most incident events (or incident IDs) for the time period selected.',
                'student_school': 'This chart shows incident events (or incident IDs) by the school where the involved student attends. View the total incident count or student rate of incidents based on the student population at that school.'
            }
            
            return tooltips[this.chartParams.chartType] || ''
        },

        countRadioLabel() {
            if (!this.incidentChart || !this.chartParams?.chartType) return 'Incident Count'
            // Incident Response uses "Response Count", everything else uses "Incident Count"
            return this.chartParams.chartType === 'incident_response' ? 'Response Count' : 'Incident Count'
        },
    },

    watch: {
        localConfig() {
            this.mergeConfigs()
        },

        extraConfigs() {
            this.mergeConfigs()
        },

        viewMode() {
            // Recalculate series when view mode changes
            this.mergeConfigs()
            // Let an expanded dockable-window instance sync its selection back to the tile
            this.$emit('viewModeChange', this.viewMode)
        }
    },

    mounted() {
        // Restore the saved view mode ('count' | 'rate') for pinned incident charts.
        if (this.initialViewMode === 'count' || this.initialViewMode === 'rate') {
            this.viewMode = this.initialViewMode
        }

        this.rangeItems = this.rangeItems.concat({
            name: this.currentYear.name,
            value: null,
            schoolYearId: this.currentYear.id
        })

        let year = this.config.baseParams.school_year_id || []
        if (!Array.isArray(year)) year = [year]

        if (this.config.baseParams.start_date && this.config.baseParams.end_date) {
            let start = this.config.baseParams.start_date
            let end = this.config.baseParams.end_date
            this.rangeItems.unshift({
                name: start + ' to ' + end,
                start: start,
                end: end
            })
        } else if (year.length) {
            let yr = ''
            // set the years that the user selected
            year.forEach((y, i) => {
                if (y) {
                    let f = this.$store.state.global.shareableStores.school_years.find(r => r.id === parseInt(y))
                    if (i) yr += ', '
                    yr += f.name
                } else {
                    yr = 'Custom Search'
                }

            })
            this.rangeItems.unshift({
                name: yr,
                value: null,
                schoolYearId: year
            })
        }

        if (this.config.baseParams.range && this.config.baseParams.range.value) {
            this.selectedRange = this.rangeItems.find(x => x.value === this.config.baseParams.range.value)
        } else {
            this.selectedRange = this.rangeItems[0]
        }

        this.localConfig = {...this.config}
        if (this.config.chartType === 'student') { // students chart type does not include fields
            let studentNames = []
            this.localConfig.config.data.forEach(r => {
                if (studentNames.indexOf(r.student_full_name) < 0) studentNames.push(r.student_full_name)
            })
            this.localConfig.categories = studentNames
        }
        this.categories = [...this.localConfig.categories]

        this.chartParams = {...this.localConfig}

        this.extraConfigs.series = this.series

        this.$emit('all-charts', this.$refs.bigFiveChart)

        if (this.collectionChart) {
            this.updateSingleChart(this.selectedRange, false)
        }

    },

    methods: {
        formatRate(value) {
            // Remove leading zero from decimal values (0.08 → .08)
            if (value == null || value === '') return ''
            return value.toString().replace(/^0\./, '.') + '%'
        },

        formatRateDisplay(value) {
            // Numeric rate for tooltips (two decimals). Use optional chaining for safety.
            const n = value != null ? Number(value) : NaN
            if (Number.isNaN(n)) return ''
            // Drop leading zero so 0.08 displays as .08% (matches formatRate and keeps tooltip compact)
            return n.toFixed(2).replace(/^0\./, '.') + '%'
        },

        getIncidentTooltipWidthFromChart(chart) {
            const w = chart?.chartWidth
            if (w == null || typeof w !== 'number') return TOOLTIP_WIDTH_MID
            let width
            if (w < CHART_WIDTH_NARROW_MAX) width = TOOLTIP_WIDTH_NARROW
            else if (w <= CHART_WIDTH_MID_MAX) width = TOOLTIP_WIDTH_MID
            else width = TOOLTIP_WIDTH_WIDE
            return Math.min(width, Math.floor(w * TOOLTIP_WIDTH_MAX_RATIO))
        },

        /**
         * Build cumulative rate line series for incident_month chart in rate view.
         * Returns the series config or null if not applicable.
         */
        buildCumulativeRateSeries() {
            if (!this.incidentChart || !this.showViewModeToggle || this.viewMode !== 'rate' ||
                this.chartParams?.chartType !== 'incident_month') return null
            const data = this.localConfig?.config?.data
            if (!data?.length) return null
            const first = data[0]
            if (!first?.year_start) return null

            // Resolve school year range and which calendar year each month falls in
            const rangeStart = this.$dayjs(first.year_start)
            const rangeEnd = this.$dayjs(first.year_end)
            const yearForMonth = (start, end, monthNum) => {
                const firstInStart = start.clone().month(monthNum - 1).startOf('month')
                const firstInEnd = end.clone().month(monthNum - 1).startOf('month')
                if (!firstInStart.isBefore(start) && !firstInStart.isAfter(end)) return firstInStart.year()
                if (!firstInEnd.isBefore(start) && !firstInEnd.isAfter(end)) return firstInEnd.year()
                return start.year()
            }

            // Sort records chronologically (month + year within school year)
            const sortedData = [...data].sort((a, b) => {
                const aMonth = MONTH_NAME_MAP[a.incident_month_name] || a.incident_month
                const bMonth = MONTH_NAME_MAP[b.incident_month_name] || b.incident_month
                const aYear = yearForMonth(rangeStart, rangeEnd, aMonth)
                const bYear = yearForMonth(rangeStart, rangeEnd, bMonth)
                return (aYear * 100 + aMonth) - (bYear * 100 + bMonth)
            })

            // Use the server-computed year-to-date cumulative rate. It counts each student only once
            // (from their first incident month onward), so it can't exceed 100%. (Previously this was
            // summed from per-month rates on the client, which double-counted multi-month students.)
            const me = this
            const today = this.$dayjs()
            const startDateLabel = rangeStart.format('M/D/YYYY')
            const cumulativeMap = new Map()
            for (const rec of sortedData) {
                const rateValue = Math.round((parseFloat(rec.cumulative_rate) || 0) * 100) / 100
                const monthName = rec.incident_month_name
                const monthNum = MONTH_NAME_MAP[monthName] ?? rec.incident_month
                const recStart = this.$dayjs(rec.year_start)
                const recEnd = this.$dayjs(rec.year_end)
                const year = yearForMonth(recStart, recEnd, monthNum)
                const firstOfMonth = recStart.clone().year(year).month(monthNum - 1).startOf('month')
                const endDate = today.isSame(firstOfMonth, 'month')
                    ? this.$dayjs().subtract(1, 'day').format('M/D/YYYY')
                    : firstOfMonth.endOf('month').format('M/D/YYYY')
                cumulativeMap.set(monthName, { y: rateValue, startDate: startDateLabel, endDate, monthName })
            }

            // Order points to match categories and return series config
            const order = this.categories.length ? this.categories : sortedData.map(r => r.incident_month_name)
            const cumulativeData = order.map(key => cumulativeMap.get(key)).filter(Boolean)

            return {
                type: 'line',
                name: 'Cumulative Rate',
                color: '#5A53C9',
                data: cumulativeData.map((d) => ({ y: d.y, customData: d })),
                marker: { enabled: false },
                dataLabels: { enabled: false },
                tooltip: {
                    formatter: function() {
                        const pointData = this.point.customData
                        if (!pointData) return ''
                        const tooltipWidth = me.getIncidentTooltipWidthFromChart(this.series?.chart)
                        const rateDisplay = me.formatRateDisplay(pointData.y)
                        const content = `<b>${pointData.startDate} - ${pointData.endDate}</b><br/>Cumulative Rate: <b>${rateDisplay} of students</b><br/><span style="color: #495B8F;"><i>Displaying the cumulative rate of students with at least one incident from ${pointData.startDate} to ${pointData.endDate}.</i></span>`
                        return '<div style="width: ' + tooltipWidth + 'px; white-space: normal; overflow-wrap: break-word;">' + content + '</div>'
                    }
                }
            }
        },

        doExpand() {
            let name = this.pinName || this.pinTitle || this.localConfig?.config?.title || 'Create Incident'
            this.$store.commit('global/addDockableWindow', {
                name: name,
                component: 'big-five-chart',
                attrs: {
                    config: this.localConfig,
                    height: window.innerHeight - 250,
                    showTools: false,
                    class: 'ma-5',
                    appliedFilters: this.appliedFilters,
                    collectionChart: this.collectionChart,
                    incidentChart: this.incidentChart,
                    initialViewMode: this.viewMode
                },
                events: {
                    viewModeChange: (mode) => {
                        this.viewMode = mode
                    }
                }
            })
        },

        minMax(count) {
            if (count < this.extraConfigs.yAxis.minimum || !this.extraConfigs.yAxis.minimum) this.extraConfigs.yAxis.minimum = count
            if (count > this.extraConfigs.yAxis.maximum || !this.extraConfigs.yAxis.maximum) this.extraConfigs.yAxis.maximum = count
        },

        buildSeries(categories, cfg) {
            let series = []

            cfg.data.forEach((rec, i) => {
                let exists = series.find(serie => {
                    return serie.name === rec.school_year_name
                })

                if (!exists) {
                    series.push({
                        name: rec.school_year_name,
                        schoolYearId: rec.school_year_id,
                        data: []
                    })
                }
            })

            series.forEach(serie => {
                categories.forEach(cat => {
                    let rec = cfg.data.find(d => d[cfg.x_field] === cat)
                    let value = this.viewMode === 'rate' ? rec.rate : rec.total_count
                    serie.data.push(value)
                })
            })

            return series
        },

        updateSingleChart(range, showLoader = true) {
            let p = {...this.config.baseParams}

            // When user changes date period (90 days, current year, etc.)
            // begin excluding any selected cohort school year so that backend
            // will not use it to limit results - we don't care about the
            // originally selected cohort school year anymore
            delete p.cohort_school_year_id

            if (range.start) {
                p.start_date = range.start
                p.end_date = range.end
            } else {
                p.start_date = range.value ? this.$dayjs().subtract(range.value, 'd').format('YYYY-MM-DD') : null
            }

            if (p.start_date) delete p.school_year_id
            else if (range.schoolYearId) p.school_year_id = range.schoolYearId

            let reloadParams = {...p}
            reloadParams.chart_types = [this.config.chartType]
            reloadParams.range = p.range
            delete reloadParams.saved_search_id
            this.showLoader = showLoader

            this.$axios.get(this.$models.getUrl('studentIncidentChart', 'read') + '&' + this.$objectToParams(reloadParams))
                .then(response => {
                    let data = this.$ecResponse(response, 'incidents')
                    let chart = data.charts[0]
                    let categories = chart.fields ? chart.fields : []

                    if (chart.chart_type === 'student') { // students chart type does not include fields
                        let studentNames = []
                        chart.data.forEach(r => {
                            if (studentNames.indexOf(r.student_full_name) < 0) studentNames.push(r.student_full_name)
                        })
                        this.categories = studentNames
                        categories = [...this.categories]
                    } else {
                        this.categories = categories
                    }

                    this.localConfig = {
                        title: chart.title,
                        chartType: chart.chart_type,
                        categories: categories,
                        config: chart,
                        baseParams: reloadParams,
                        appliedFilters: chart.filters_applied
                    }
                    this.showLoader = false
                })
        },

        mergeConfigs() {
            this.updatedLocalConfig = {}
            let o1 = this.localConfig.config
            let o2 = this.extraConfigs
            let objMerge = {}
            
            if (this.incidentChart) {
                let seriesData = this.series || []
                let maxValue = 0
                seriesData.forEach(serie => {
                    serie.data.forEach(val => {
                        // Handle both number values and object values (for cumulative line)
                        let value = typeof val === 'object' && val.y !== undefined ? val.y : val
                        
                        // Rate values from backend are already percentages (0.08 = 0.08%, 2.39 = 2.39%)
                        // No normalization needed
                        if (value > maxValue) maxValue = value
                    })
                })
                
                if (this.showViewModeToggle && this.viewMode === 'rate') {
                    // Calculate explicit maximum with padding to ensure data labels stay above bars
                    let calculatedMax = maxValue * (1 + 0.15)
                    let rateYAxis = Object.assign({}, o2.yAxis, {
                        maximum: calculatedMax,
                        maxPadding: 0.15,
                        endOnTick: true,
                        tickInterval: maxValue <= 10 ? 1 : 1.5,
                        title: {
                            text: 'Student Rate'
                        }
                    })
                    o2.yAxis = rateYAxis
                } else {
                    let roundedMax = Math.ceil(maxValue * 1.1)
                    let tickInterval
                    if (roundedMax <= 50) {
                        tickInterval = 10
                    } else if (roundedMax <= 200) {
                        tickInterval = 25
                    } else if (roundedMax <= 500) {
                        tickInterval = 50
                    } else if (roundedMax <= 1000) {
                        tickInterval = 100
                    } else if (roundedMax <= 5000) {
                        tickInterval = 500
                    } else {
                        tickInterval = 1000
                    }
                    
                    let countYAxis = Object.assign({}, o2.yAxis, {
                        maximum: roundedMax,
                        maxPadding: 0.10,
                        endOnTick: true,
                        tickInterval: tickInterval,
                        title: {
                            text: this.chartParams.chartType === 'incident_response' ? 'Response Count' : 'Incident Count'
                        }
                    })
                    o2.yAxis = countYAxis
                }
            }
            
            Object.keys(o2).forEach(key => {
                if (key === 'title') {
                    let text = o1.title
                    objMerge.title = Object.assign({}, {text}, o2.title)
                } else if (o2[key] instanceof Object) {
                    if (key === 'yAxis' && this.incidentChart && this.showViewModeToggle && this.viewMode === 'rate') {
                        objMerge[key] = Object.assign({}, o2[key])
                    } else {
                        objMerge[key] = Object.assign({}, o1[key], o2[key]);
                    }
                    // Don't override tooltip formatter for non-incident charts (e.g. District Search)
                    // so they keep the default tooltip format
                    if (key === 'tooltip' && !this.incidentChart && objMerge.tooltip) {
                        delete objMerge.tooltip.formatter
                    }
                } else {
                    objMerge[key] = o2[key];
                }
            })
            Object.assign(this.updatedLocalConfig, objMerge)
            this.updatedLocalConfig.xAxis.categories = this.localConfig.categories || []
            this.updatedLocalConfig.series = this.localConfig.series || this.series || []
        }
    }
}
</script>

<style lang="scss" scoped>
.tool-slot {
    position: absolute;
    top: 15px;
    right: 55px;
}

.fe-chart ::v-deep * {
    font-family: 'HyperlegibleSans-Regular', Hyperlegible Sans, 'Roboto', sans-serif !important;
    color: #dddddd !important;
}

.big5-date-picker {
    position: absolute;
    top: 60px;
    right: 15px;

    &-collection {
        float: right;
        right: -140px;
    }
}

.loader {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    background: #404040;
    opacity: .5;

    &-spinner {
        position: relative;
        left: 46%;
        top: 45%;
    }
}

.with-border {
    border: 1px solid #dddddd;
    border-radius: 4px;
    position: relative;
}

.show-more-filters-btn {
    align-items: center;
    display: flex;
    color: #006C90;
    font-size: 12px;
    cursor: pointer;
}

.chart-view-mode-toggle {
    padding: 8px 0 0;
}

.chart-view-mode-toggle .mt-3 {
    margin-top: 0px !important;
}

.chart-with-filters {
    position: relative;
    top: -45px;
    flex-wrap: wrap;
    width: 95%;
    margin-left: 12px;
    margin-bottom: -35px;

    ::v-deep .v-chip[hoverable] .v-chip__content {
        color: #050f2d;
        background-color: rgb(224, 225, 230);
    }
}
</style>
